import { CartItem, CartTotal } from "../features/cart/cartModels";
import {
  EditOrderItem,
  OrderItem,
  OrderTotal,
} from "../features/order/orderModels";
import { Tax } from "../features/restaurant/restaurantModels";
import { getDecimalPlacedPrice } from "./menu-helper-utils";

const getCalculatedValue = (amount: number, value: any) => {
  if (value.type == "FLATFEE") {
    return value.rate;
  } else if (value.type == "PERCENT") {
    return amount * (value.rate / 100);
  } else {
    return 0;
  }
};

export const getItemTax = (item: CartItem, serviceCharge: any) => {
  return item.tax.reduce((accumulator: number, currentTax: Tax) => {
    let itemPrice = getDecimalPlacedPrice(item.subTotal);
    const serviceFee =
      serviceCharge.length > 0
        ? getCalculatedValue(itemPrice, serviceCharge[0])
        : 0;

    if (currentTax.type == "FLATFEE") {
      return accumulator + currentTax.rate * +item.quantity;
    } else if (currentTax.type == "PERCENT") {
      let taxPrice = ((itemPrice + serviceFee) * currentTax.rate) / 100;
      return accumulator + taxPrice;
    } else {
      return accumulator;
    }
  }, 0);
};

export const getEditCartItemFromCartItem = (
  items: CartItem[],
  modifiedItemIds: string[]
): any[] => {
  return items.map((item) => {
    return {
      ...item,
      orderItemId: modifiedItemIds.includes(item.itemId) ? item.itemId : null,
      id: item.itemId,
      taxFees: "0.0",
      isItemModified: modifiedItemIds.includes(item.itemId),
      options: item.options.map((option) => {
        return {
          ...option,
          //id: option.modifierOptionId,
          isOptionModified: false,
        };
      }),
    };
  });
};

export const getEditCartItemFromOrderItem = (
  items: OrderItem[],
  modifiedItemIds: string[]
): EditOrderItem[] => {
  return items.map((item) => {
    return {
      ...item,
      taxFees: item.taxFees || "0.0",
      isItemModified: modifiedItemIds.includes(item.id),
      options: item.options.map((option) => {
        return {
          ...option,
          //id: option.id,
          isOptionModified: false,
        };
      }),
    };
  });
};

export const getTotalByCode = (
  totals: CartTotal[] | OrderTotal[],
  code: string
): CartTotal | OrderTotal | null => {
  let totalIndex = totals?.findIndex((total) => +total.code == +code);
  if (totalIndex >= 0) {
    return totals[totalIndex];
  } else {
    return null;
  }
};

export const getTitleByCode = (
totals: CartTotal[] | OrderTotal[],
code: string,
): CartTotal | OrderTotal | null => {
let totalIndex = totals?.findIndex((total) => Number(+total.code) == Number(+code));
if (totalIndex >= 0) {
  return totals[totalIndex];
} else {
  return null;
}
};

type DeliveryFeeTotalsArgs = {
  hasActiveDeliveryAddress: boolean;
  restaurantCountry?: string | null;
  minAmountForFreeDelivery?: number | string | null;
  orderSubTotal: number;
  quotedDeliveryFee?: number | string | null;
  fallbackDeliveryFee?: number;
};

export const getDeliveryFeeForTotals = ({
  hasActiveDeliveryAddress,
  restaurantCountry,
  minAmountForFreeDelivery,
  orderSubTotal,
  quotedDeliveryFee,
  fallbackDeliveryFee = 0,
}: DeliveryFeeTotalsArgs): number => {
  if (!hasActiveDeliveryAddress) {
    return 0;
  }

  const parsedQuotedDeliveryFee = Number(quotedDeliveryFee);
  const safeQuotedDeliveryFee = Number.isFinite(parsedQuotedDeliveryFee)
    ? parsedQuotedDeliveryFee
    : 0;
  const parsedMinAmountForFreeDelivery = Number(minAmountForFreeDelivery);
  const hasMinAmountForFreeDelivery =
    minAmountForFreeDelivery !== null &&
    minAmountForFreeDelivery !== undefined &&
    minAmountForFreeDelivery !== "" &&
    !Number.isNaN(parsedMinAmountForFreeDelivery);

  if (restaurantCountry === "US") {
    if (hasMinAmountForFreeDelivery) {
      return parsedMinAmountForFreeDelivery > orderSubTotal
        ? safeQuotedDeliveryFee
        : 0;
    }

    return safeQuotedDeliveryFee;
  }

  if (hasMinAmountForFreeDelivery) {
    return parsedMinAmountForFreeDelivery > orderSubTotal
      ? safeQuotedDeliveryFee
      : 0;
  }

  return fallbackDeliveryFee;
};

export const mergeTotals = (
  cartTotals: CartTotal[],
  orderTotals: OrderTotal[]
): OrderTotal[] => {
  return orderTotals.map((total) => {
    const cartTotal = getTotalByCode(cartTotals, total.code);
    if (cartTotal) {
      return {
        ...total,
        value: (+total.value + +cartTotal.value).toString(),
      };
    } else {
      return total;
    }
  });
};

/**
 * The location's delivery provider id, however the caller happens to hold it.
 *
 * This exists because `DeliveryProvider` means two different things depending
 * on where you read it. The outer one is the object off the restaurant slice;
 * several handlers then declare an inner `DeliveryProvider` that shadows it
 * and holds the id *string*. Payload builders kept doing
 * `DeliveryProvider?.deliveryProviderId ?? 'DOORDASH'`, which reads a property
 * that a string does not have — so it silently resolved to `'DOORDASH'` for
 * every location, every time.
 *
 * That is not a cosmetic slip. A Burq location sent as `DOORDASH` misses the
 * saga's Burq branch, quotes against the courier through the gateway, and the
 * gateway caches that price under `locationId:customerId:addressId` for ten
 * minutes — after which the totals call prefers the cache over the Burq band
 * price and the diner is shown the wrong delivery fee.
 *
 * Accepting both shapes means a future shadow cannot reintroduce it.
 */
export const toDeliveryProviderId = (
  source: unknown,
  fallback = "DOORDASH"
): string => {
  if (!source) return fallback;

  if (typeof source === "string") {
    return source.trim() || fallback;
  }

  const id = (source as { deliveryProviderId?: unknown })?.deliveryProviderId;
  return typeof id === "string" && id.trim() ? id.trim() : fallback;
};
